import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, authQuery, API, downloadUrl } from "../lib/api";
import { Archive, Plus, Lock, Trash2, X, FileDown } from "lucide-react";

/**
 * Faza 4 — Vot electronic secret (admin side).
 * Venerabilul deschide un scrutin (balotaj cu bile albe/negre sau vot cu
 * opțiuni multiple), frații votează anonim din aplicația mobilă, iar
 * rezultatul rămâne SIGILAT până la închidere — nici adminul nu vede
 * distribuția cât timp votul e deschis.
 */
export default function VotesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", type: "ballot", options: "", eligible_degree: 3 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/votes");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/votes", {
        title: form.title,
        description: form.description || undefined,
        type: form.type,
        options: form.type === "choice" ? form.options.split("\n").map((x) => x.trim()).filter(Boolean) : [],
        eligible_degree: Number(form.eligible_degree),
      });
      toast.success(t("votes.openedOk"));
      setShowForm(false);
      setForm({ title: "", description: "", type: "ballot", options: "", eligible_degree: 3 });
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const close = async (v) => {
    if (!window.confirm(`${t("votes.closeConfirm")} «${v.title}»?`)) return;
    setBusy(true);
    try {
      await api.post(`/votes/${v.id}/close`);
      toast.success(t("votes.closedOk"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`${t("votes.deleteConfirm")} «${v.title}»?`)) return;
    try {
      await api.delete(`/votes/${v.id}`);
      toast.success(t("common.deleted", "Șters"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const degLabel = (d) => t(`planse.deg${Math.min(Math.max(d || 1, 1), 3)}`);

  const renderResults = (v) => {
    const tally = v.results?.tally || {};
    const total = v.results?.total || 0;
    const keys = v.type === "ballot" ? ["white", "black"] : v.options;
    return (
      <div style={{ marginTop: 10 }} data-testid={`results-${v.id}`}>
        {keys.map((k) => {
          const n = tally[k] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          const label = v.type === "ballot" ? (k === "white" ? `⚪ ${t("votes.whiteBall")}` : `⚫ ${t("votes.blackBall")}`) : k;
          return (
            <div key={k} style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem" }}>
                <span>{label}</span>
                <strong>{n} · {pct}%</strong>
              </div>
              <div style={{ height: 7, background: "#eee7d6", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: 7, background: k === "black" ? "#222" : "#C8A96A" }} />
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: "0.72rem", color: "#8a8470", marginTop: 4 }}>
          {t("votes.totalBallots")}: {total} / {v.eligible_count}
        </div>
        {v.pdf_doc_id && (
          <button
            className="btn-outline-gold flex items-center gap-1"
            style={{ marginTop: 10 }}
            onClick={() => downloadUrl(
              `${API}/documents/${v.pdf_doc_id}/download${authQuery()}`,
              `proces-verbal-${(v.title || "vot").replace(/[^\w\- ]/g, "").trim().replace(/ +/g, "_")}.pdf`
            )}
            data-testid={`pv-download-${v.id}`}
          >
            <FileDown size={12} /> {t("votes.pdfBtn")}
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-testid="votes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Archive size={22} /> {t("votes.title")}
          </h1>
          <p className="page-subtitle">{t("votes.subtitle")}</p>
        </div>
        <button className="btn-primary-gold flex items-center gap-1" onClick={() => setShowForm(true)} data-testid="vote-new-btn">
          <Plus size={13} /> {t("votes.newBtn")}
        </button>
      </div>

      <div className="mt-8 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))" }}>
        {rows.length === 0 && (
          <p style={{ color: "#8a8470", fontStyle: "italic" }}>{t("votes.empty")}</p>
        )}
        {rows.map((v) => (
          <div key={v.id} className="lodge-card" style={{ padding: 18 }} data-testid={`vote-card-${v.id}`}>
            <div className="flex items-center justify-between">
              <span className={v.status === "open" ? "badge-grade" : "badge-status inactive"}>
                {v.status === "open" ? t("votes.statusOpen") : t("votes.statusClosed")}
              </span>
              <div className="flex gap-2">
                {v.status === "open" && (
                  <button
                    className="btn-primary-gold flex items-center gap-1"
                    onClick={() => close(v)}
                    disabled={busy}
                    data-testid={`close-btn-${v.id}`}
                  >
                    <Lock size={12} /> {t("votes.closeBtn")}
                  </button>
                )}
                <button className="btn-danger" onClick={() => remove(v)} data-testid={`delete-vote-${v.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <h3 style={{ fontWeight: 700, marginTop: 10 }}>{v.title}</h3>
            {v.description && <p style={{ fontSize: "0.8rem", color: "#6b6557", marginTop: 4 }}>{v.description}</p>}
            <div style={{ fontSize: "0.74rem", color: "#8a8470", marginTop: 8 }}>
              {t("votes.eligibleDegree")}: {degLabel(v.eligible_degree)} ·{" "}
              {t("votes.turnout")}: <strong>{v.voters_count} / {v.eligible_count}</strong>
            </div>
            {v.status === "open" ? (
              <div
                style={{
                  marginTop: 10, padding: "8px 12px", borderRadius: 6,
                  background: "#faf7f0", border: "1px dashed #cfc9b7",
                  fontSize: "0.75rem", color: "#6b6557", fontStyle: "italic",
                }}
                data-testid={`sealed-${v.id}`}
              >
                🔒 {t("votes.sealedNote")}
              </div>
            ) : renderResults(v)}
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="vote-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title">{t("votes.formTitle")}</h2>
              <button className="btn-outline-gold" onClick={() => setShowForm(false)}><X size={14} /></button>
            </div>
            <form onSubmit={create} className="grid gap-3">
              <div>
                <label className="kv-label">{t("votes.fieldTitle")} *</label>
                <input
                  className="input-lodge" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t("votes.fieldTitlePh")}
                  data-testid="vote-title-input"
                />
              </div>
              <div>
                <label className="kv-label">{t("votes.fieldDesc")}</label>
                <textarea
                  className="input-lodge" rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="kv-label">{t("votes.fieldType")}</label>
                  <select
                    className="input-lodge" value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    data-testid="vote-type-select"
                  >
                    <option value="ballot">{t("votes.typeBallot")}</option>
                    <option value="choice">{t("votes.typeChoice")}</option>
                  </select>
                </div>
                <div>
                  <label className="kv-label">{t("votes.fieldDegree")}</label>
                  <select
                    className="input-lodge" value={form.eligible_degree}
                    onChange={(e) => setForm({ ...form, eligible_degree: e.target.value })}
                    data-testid="vote-degree-select"
                  >
                    <option value={1}>{t("planse.deg1")}+</option>
                    <option value={2}>{t("planse.deg2")}+</option>
                    <option value={3}>{t("planse.deg3")}</option>
                  </select>
                </div>
              </div>
              {form.type === "choice" && (
                <div>
                  <label className="kv-label">{t("votes.fieldOptions")}</label>
                  <textarea
                    className="input-lodge" rows={4} value={form.options}
                    onChange={(e) => setForm({ ...form, options: e.target.value })}
                    placeholder={t("votes.fieldOptionsPh")}
                    data-testid="vote-options-input"
                  />
                </div>
              )}
              <div className="flex justify-end gap-3 mt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
                <button type="submit" className="btn-primary-gold" disabled={busy} data-testid="vote-form-save">
                  {t("votes.openBtn")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
